import { useMemo } from "react";

import { renderPointMarker, useHotspotCanvas } from "@vericus/cadmus-common";

import { useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot } from "@/features/authority";
import { PromptImage } from "@/generated/graphql";
import { useAnswerBlock } from "@/graphql/hooks";

export const useHotspotTextChoiceResponseForm = (answerBlockId: string) => {
  const { data } = useAnswerBlock(answerBlockId);
  const promptImage = useMemo(() => data?.question?.body?.promptImage, [data]);
  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const savedAnswerMap = useMemo(() => {
    return data?.question?.body?.fields?.reduce(
      (acc, field) => {
        if (field) {
          if (field.identifier) {
            const answer = snapshot?.answerBlanks?.[field.identifier] || "";
            if (
              field.interaction &&
              field.interaction.__typename === "ChoiceInteraction"
            ) {
              const choice = field.interaction.choices?.find(
                (choice) => choice?.identifier === answer
              );
              acc[field.identifier] = choice ? (choice.content ?? "") : "";
            } else {
              acc[field.identifier] = answer;
            }
          }
        }
        return acc;
      },
      {} as Record<string, string>
    );
  }, [data, snapshot]);

  const renderPoints = (context: CanvasRenderingContext2D, scale: number) => {
    if (context) {
      promptImage?.layers?.forEach((layer, index) => {
        if (!layer || !layer.identifier || !layer.coords) {
          return;
        }
        const [x = 0, y = 0] = layer.coords;
        const savedAnswer = savedAnswerMap?.[layer.identifier]
          ? `. ${savedAnswerMap[layer.identifier]}`
          : "";
        renderPointMarker(context, scale, `${index + 1}${savedAnswer}`, [x, y]);
      });
    }
  };

  const { canvasRef, wrapperRef } = useHotspotCanvas(
    promptImage as PromptImage,
    (ctx, scale) => {
      renderPoints(ctx, scale);
    }
  );

  return {
    canvasRef,
    wrapperRef,
    promptImage,
  };
};
